import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  BookingCalendar,
  TimeSelector,
  HoursSelector,
  MultiLockerSelector,
  MultiBookingSummary,
  PersonalDataForm,
  LOCKER_SIZES_MULTI as LOCKER_SIZES
} from './booking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose, initialSize = 'medium' }) => {
  const { i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [hours, setHours] = useState(2);
  const [lockerQuantities, setLockerQuantities] = useState({ small: 0, medium: 0, xlarge: 0 });
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDate(null);
      setSelectedTime(null);
      setHours(2);
      // Pre-select 1 of the initial size
      setLockerQuantities({ 
        small: initialSize === 'small' ? 1 : 0, 
        medium: initialSize === 'medium' ? 1 : 0, 
        xlarge: initialSize === 'xlarge' ? 1 : 0 
      });
      setFormData({ name: '', email: '', phone: '' });
      setError(null);
      setBookingResult(null);
      setCurrentMonth(new Date());
    }
  }, [isOpen, initialSize]);

  // Calculate totals
  const selectedLockers = LOCKER_SIZES.filter(l => (lockerQuantities[l.id] || 0) > 0);
  const totalLockersCount = Object.values(lockerQuantities).reduce((sum, qty) => sum + qty, 0);
  const pricePerHour = selectedLockers.reduce((sum, l) => sum + (l.price * (lockerQuantities[l.id] || 0)), 0);
  const totalPrice = pricePerHour * hours;

  const formatDate = (date) => {
    if (!date) return '';
    // Use local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleTimeSelect = (time) => {
    const startHour = parseInt(time.split(':')[0]);
    if (startHour + hours > 23) {
      setError(`Con ${hours} ore, il deposito finirebbe dopo le 23:00. Scegli un orario precedente o riduci le ore.`);
      return;
    }
    setError(null);
    setSelectedTime(time);
    
    setTimeout(() => {
      const hoursSection = document.getElementById('hours-selector');
      if (hoursSection) {
        hoursSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleHoursChange = (newHours) => {
    if (selectedTime) {
      const startHour = parseInt(selectedTime.split(':')[0]);
      if (startHour + newHours > 23) {
        setError(`Con ${newHours} ore e inizio alle ${selectedTime}, il deposito finirebbe dopo le 23:00.`);
        return;
      }
    }
    setError(null);
    setHours(newHours);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Compila tutti i campi');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build lockers array for multi-size booking
      const lockers = [];
      for (const [sizeId, qty] of Object.entries(lockerQuantities)) {
        if (qty > 0) {
          lockers.push({ size: sizeId, quantity: qty });
        }
      }

      const response = await axios.post(`${API_URL}/api/locker/book-multi`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        lockers: lockers,
        date: formatDate(selectedDate),
        start_time: selectedTime,
        hours: hours,
        origin_url: window.location.origin,
        lang: i18n.language || 'it'
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setError('Errore: URL di pagamento non ricevuto');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la prenotazione. Riprova.');
      setLoading(false);
    }
  };

  // Build lockers summary string for confirmation
  const getLockersSummaryString = () => {
    return selectedLockers.map(l => `${lockerQuantities[l.id]}× ${l.name}`).join(' + ');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-[#141414] border border-[#2A2A2A] w-full max-w-lg max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#141414] border-b border-[#2A2A2A] p-4 flex items-center justify-between z-10">
            <h2 className="font-heading text-xl font-bold text-white">
              {step === 4 ? 'Prenotazione Confermata!' : 'Prenota i tuoi Locker'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              data-testid="close-booking-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Steps */}
          {step < 4 && (
            <div className="px-4 pt-4">
              <div className="flex items-center justify-between mb-6">
                {[1, 2, 3].map((s) => (
                  <div key={`step-${s}`} className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                        step >= s
                          ? 'bg-[#FFCC00] text-[#0A0A0A]'
                          : 'bg-[#2A2A2A] text-gray-500'
                      }`}
                    >
                      {step > s ? <Check className="w-4 h-4" /> : s}
                    </div>
                    {s < 3 && (
                      <div
                        className={`w-16 sm:w-24 h-0.5 mx-2 transition-colors ${
                          step > s ? 'bg-[#FFCC00]' : 'bg-[#2A2A2A]'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mb-4">
                <span>Data & Ora</span>
                <span>Locker</span>
                <span>Dati</span>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-4">
            {/* Step 1: Date & Time */}
            {step === 1 && (
              <div className="space-y-6">
                <BookingCalendar
                  currentMonth={currentMonth}
                  setCurrentMonth={setCurrentMonth}
                  selectedDate={selectedDate}
                  onDateSelect={setSelectedDate}
                />

                {selectedDate && (
                  <TimeSelector
                    selectedTime={selectedTime}
                    onTimeSelect={handleTimeSelect}
                  />
                )}

                {selectedTime && (
                  <HoursSelector
                    hours={hours}
                    selectedTime={selectedTime}
                    onHoursChange={handleHoursChange}
                  />
                )}

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}
              </div>
            )}

            {/* Step 2: Multi Locker Selection */}
            {step === 2 && (
              <div>
                <MultiLockerSelector
                  lockerQuantities={lockerQuantities}
                  onQuantityChange={setLockerQuantities}
                />

                <MultiBookingSummary
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  hours={hours}
                  lockerQuantities={lockerQuantities}
                />
              </div>
            )}

            {/* Step 3: Personal Data */}
            {step === 3 && (
              <div>
                <PersonalDataForm
                  formData={formData}
                  setFormData={setFormData}
                />

                <MultiBookingSummary
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  hours={hours}
                  lockerQuantities={lockerQuantities}
                  showFull
                />

                {error && (
                  <p className="text-red-500 text-sm mt-4">{error}</p>
                )}
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && bookingResult && (
              <div className="text-center py-6">
                <div className="w-20 h-20 bg-[#FFCC00] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-[#0A0A0A]" />
                </div>
                <h3 className="text-white text-2xl font-bold mb-2">Prenotazione Confermata!</h3>
                <p className="text-gray-400 mb-6">Il tuo codice di prenotazione è:</p>
                
                <div className="bg-[#0A0A0A] border-2 border-[#FFCC00] p-6 mb-6">
                  <p className="text-[#FFCC00] text-4xl font-mono font-bold tracking-wider">
                    {bookingResult.booking.booking_code}
                  </p>
                </div>

                <div className="bg-[#0A0A0A] border border-[#2A2A2A] p-4 text-left mb-6">
                  <h4 className="text-white font-semibold mb-3">Dettagli Prenotazione</h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-400">
                      Data: <span className="text-white">{bookingResult.booking.date}</span>
                    </p>
                    <p className="text-gray-400">
                      Orario: <span className="text-white">{bookingResult.booking.start_time} - {bookingResult.booking.end_time}</span>
                    </p>
                    <p className="text-gray-400">
                      Locker: <span className="text-white">{bookingResult.booking.lockers_summary}</span>
                    </p>
                    <p className="text-gray-400">
                      Totale: <span className="text-[#FFCC00] font-bold">€{bookingResult.booking.total_price.toFixed(2).replace('.', ',')}</span>
                    </p>
                  </div>
                </div>

                <p className="text-gray-400 text-sm mb-6">
                  Abbiamo inviato i dettagli a <span className="text-white">{bookingResult.booking.email}</span>
                </p>

                <button
                  onClick={onClose}
                  className="bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide hover:bg-[#E6B800] px-8 py-4 transition-all duration-200"
                  data-testid="close-confirmation"
                >
                  Chiudi
                </button>
              </div>
            )}
          </div>

          {/* Footer with Navigation */}
          {step < 4 && (
            <div className="sticky bottom-0 bg-[#141414] border-t border-[#2A2A2A] p-4 flex gap-4">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex-1 bg-transparent text-white border border-[#2A2A2A] hover:border-[#FFCC00] px-6 py-3 font-semibold transition-colors"
                >
                  Indietro
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 1 && (!selectedDate || !selectedTime)) || 
                    (step === 2 && totalLockersCount === 0) ||
                    loading
                  }
                  className="flex-1 bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide hover:bg-[#E6B800] px-6 py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="next-step"
                >
                  Continua
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || !formData.name || !formData.email || !formData.phone}
                  className="flex-1 bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide hover:bg-[#E6B800] px-6 py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-booking"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Reindirizzamento al pagamento...
                    </>
                  ) : (
                    <>
                      Procedi al Pagamento - €{totalPrice.toFixed(2).replace('.', ',')}
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookingModal;
